import FleetTitle from '@/libs/antd/components/Title';
import { FC, Fragment, useState } from 'react';
import { useFRMPraSurveyInput } from './PraSurveyInputPage';
import {
	Alert,
	Col,
	DatePicker,
	Divider,
	Form,
	Input,
	Row,
	Table,
	Upload,
	UploadFile,
} from 'antd';
import {
	LoadingOutlined,
	FileSearchOutlined,
	UploadOutlined,
	EditOutlined,
} from '@ant-design/icons';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { CustomerLegalDocument } from '@/libs/fleet-api/services/types';
import FleetModal from '@/libs/antd/components/Modal';
import rule from '@/libs/antd/rules';
import dayjs from 'dayjs';
import { uploader } from '@/libs/fleet-api/uploader';
import { useMutation } from '@tanstack/react-query';
import { useModals } from '@/components/modals/ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
// import { useApp } from '@/App';

type FRMPraSurveyInputLegalDocumentProps = {
	valid?: boolean;
	customerId?: string;
};
type LegalDocumentItem = CustomerLegalDocument<'payload'> &
	Pick<
		CustomerLegalDocument,
		| 'documentType'
		| 'isMandatory'
		| 'file'
		| 'isMandatory2'
		| 'isDocumentNoMandatory'
	>;

type LegalDocumentForm = {
	input?: LegalDocumentItem;
	items: LegalDocumentItem[];
};

export const useFRMPraSurveyInputLegalDocumentForm = () => {
	return useForm<LegalDocumentForm>();
};

const FRMPraSurveyInputLegalDocument: FC<
	FRMPraSurveyInputLegalDocumentProps
> = ({ valid = false }) => {
	const { legalDocumentForm, customerLegalDocuments, mainInformation } =
		useFRMPraSurveyInput();

	const { form, fieldName } = legalDocumentForm;
	const [activeIndexModal, setActiveIndexModal] = useState<number>();
	// const { navigate } = useApp();
	const { openPreviewModal } = useModals();

	const handleSubmit = ({ input }: LegalDocumentForm) => {
		const items = form.getFieldValue(fieldName('items')) as LegalDocumentItem[];
		form.setFieldsValue({
			items: items.map((item, index) => {
				if (index === activeIndexModal) return input;
				return item;
			}),
		} as LegalDocumentForm);
		setActiveIndexModal(undefined);
		form.resetFields([fieldName(['input'])]);
	};

	const getInitialValues = () => {
		return {
			items:
				customerLegalDocuments?.map((data) => {
					return {
						id: data.id,
						documentNo: data.documentNo,
						documentDate: data.documentDate,
						documentType: data.documentType,
						file: data.file,
						isMandatory: data.isMandatory,
						isMandatory2: data.isMandatory2,
					} as LegalDocumentItem;
				}) ?? [],
			input: undefined,
		} as LegalDocumentForm;
	};

	const uploadMutation = useMutation({
		mutationFn: uploader,
	});

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={getInitialValues()}
				onFinish={handleSubmit}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['items'])}
					noStyle
				/>

				<FleetTitle>Data Legalitas</FleetTitle>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue, resetFields }) => {
						const values = getFieldsValue() as LegalDocumentForm;

						return (
							<Fragment>
								{!valid && (
									<Alert
										message="Mohon lengkapi seluruh dokumen legalitas yang wajib (*)"
										type="warning"
										showIcon
										className="custom-alert"
										style={{ marginBottom: 20 }}
									/>
								)}
								<Table
									bordered
									dataSource={
										values?.items?.map((item, key) => {
											return { ...item, key };
										}) ?? []
									}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											width: 1,
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<a
																onClick={(e) => {
																	e.preventDefault();
																	if (!record.file?.id) return;
																	openPreviewModal(record.file as FleetFile);
																}}
																style={
																	record.file?.url
																		? { display: 'block' }
																		: {
																				pointerEvents: 'none',
																				color: '#bababa',
																				display: 'block',
																			}
																}
															>
																<FileSearchOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setActiveIndexModal(index);
																}}
															>
																<EditOutlined />
															</a>
														</div>
													</Row>
												);
											},
										},
										{
											title: 'Jenis Dokumen',
											render: (_, record) => {
												const isMandatory =
													record.isMandatory || record.isMandatory2
														? true
														: false;
												return (
													<div>
														{record.documentType}
														{isMandatory && (
															<span style={{ color: 'red' }}>*</span>
														)}
													</div>
												);
											},
										},
										{
											title: 'No Dokumen',
											render: (_, record) => record.documentNo ?? '-',
										},
										{
											title: 'Tanggal Dokumen',
											render: (_, record) =>
												record.documentDate
													? dayjs(record.documentDate).format('YYYY-MM-DD')
													: '-',
										},
										// {
										// 	title: 'Status',
										// 	align: 'center',
										// 	render: (_, record) => {
										// 		const isMandatory =
										// 			record.isMandatory || [51].includes(record.id)
										// 				? true
										// 				: false;
										// 		return (
										// 			<span
										// 				style={{
										// 					color: record.file?.url
										// 						? '#39A905'
										// 						: isMandatory
										// 							? '#e90101'
										// 							: '#ffaa00',
										// 				}}
										// 			>
										// 				{record.file?.url ? (
										// 					<CheckCircleOutlined />
										// 				) : (
										// 					<ExclamationCircleOutlined />
										// 				)}
										// 			</span>
										// 		);
										// 	},
										// },
									]}
									pagination={false}
								/>

								<FleetModal
									open={typeof activeIndexModal === 'number'}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									okButtonProps={{
										disabled: !values.input?.file?.id,
									}}
									onOk={async () => {
										const isValidForm = await form.validateFields([
											fieldName(['input']),
										]);
										if (isValidForm) {
											form?.submit();
										}
									}}
									onCancel={() => {
										setActiveIndexModal(undefined);
										resetFields([fieldName('input')]);
									}}
								>
									<FleetTitle>Edit Data Legalitas</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												label="Jenis Dokumen"
												name={fieldName(['input', 'documentType'])}
												labelCol={{ span: 8 }}
											>
												<Input.TextArea
													rows={5}
													disabled
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												label="No. Dokumen"
												name={fieldName(['input', 'documentNo'])}
												labelCol={{ span: 8 }}
												rules={
													form?.getFieldValue(
														fieldName(['input', 'isDocumentNoMandatory']),
													)
														? [rule.required()]
														: []
												}
											>
												{form?.getFieldValue(fieldName(['input', 'id'])) ===
												29 ? (
													<Input disabled />
												) : (
													<Input />
												)}
											</Form.Item>
										</Col>
									</Row>
									<Divider />
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'documentDate'])}
												label="Tanggal Dokumen"
												labelCol={{ span: 8 }}
												getValueProps={(value) => ({
													value: value && dayjs(value),
												})}
												normalize={(value) =>
													value && dayjs(value).utc(true).toISOString()
												}
											>
												<DatePicker
													disabledDate={(now) =>
														now > dayjs().subtract(1, 'day')
													}
												/>
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'file'])}
												noStyle
												required
											/>
											<Form.Item
												label="Upload Dokumen"
												required
												validateStatus={
													values.input?.file?.id ? 'success' : 'error'
												}
												help={values.input?.file?.id ? '' : 'Wajib diisi'}
											>
												<Upload.Dragger
													listType="picture"
													maxCount={1}
													fileList={
														values.input?.file
															? [
																	{
																		name: values.input.file.name,
																		uid: values.input.file.id,
																		url: values.input.file.url,
																	} as UploadFile,
																]
															: []
													}
													beforeUpload={async (file) => {
														const fileid = Date.now().toString();
														const filepath = `customer/${mainInformation!.customerId}/${mainInformation!.prospectId}/legal-documents`;

														try {
															const url = await uploadMutation.mutateAsync({
																file,
																fileid,
																filepath,
															});

															setFieldValue(fieldName(['input', 'file']), {
																format: '',
																id: fileid,
																name: file.name,
																path: filepath,
																url,
															} as LegalDocumentItem['file']);
														} catch {
															setFieldValue(fieldName(['input', 'file']), null);
														}

														return false;
													}}
													onRemove={() => {
														form.setFieldValue(
															fieldName(['input', 'file']),
															null,
														);
														return false;
													}}
												>
													{uploadMutation.isPending ? (
														<LoadingOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													) : (
														<UploadOutlined
															style={{
																fontSize: 25,
																color: '#BABABA',
																display: 'block',
															}}
														/>
													)}
												</Upload.Dragger>
											</Form.Item>
										</Col>
									</Row>
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMPraSurveyInputLegalDocument;
